// src/pages/onboarding/shared/steps/StepPasscodeCreate.jsx
import React, { useMemo, useState } from "react";
import Button from "@/components/ui/Button";
import InputOtp from "@/sot/primitives/InputOtp";
import OnboardingSection from "@/components/onboarding/OnboardingSection";
import OnboardingTopBar from "@/components/onboarding/OnboardingTopBar";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

const LS_KEY = "afrimed_passcode_pending_v1";

function savePendingPasscode(digits) {
  try {
    // store only during onboarding; confirm step will read it
    sessionStorage.setItem(LS_KEY, JSON.stringify({ v: 1, digits, t: Date.now() }));
  } catch {
    // ignore
  }
}

export default function StepPasscodeCreate({
  step,
  total,
  value, // optional controlled value
  onChange, // optional controlled setter
  onBack,
  onContinue, // will be called with the 6-digit code
  contextNote = "",
  onTelemetry,
}) {
  const { t } = useLanguage();
  const isControlled = typeof value !== "undefined" && typeof onChange === "function";
  const [local, setLocal] = useState(value ?? "");

  const pass = isControlled ? (value ?? "") : local;
  const setPass = (v) => (isControlled ? onChange(v) : setLocal(v));

  const digits = useMemo(() => String(pass || "").replace(/\D/g, ""), [pass]);
  const valid = digits.length === 6;

  const handleContinue = () => {
    onTelemetry?.("continue_clicked", { valid });
    if (!valid) {
      onTelemetry?.("validation_blocked", { reason: "passcode_incomplete" });
      return;
    }

    // ✅ realistic: keep passcode local-only (session scoped while onboarding)
    onTelemetry?.("save_started");
    savePendingPasscode(digits);
    onTelemetry?.("save_succeeded");

    onContinue?.(digits);
  };

  return (
    <>
      <OnboardingTopBar step={step} total={total} onBack={onBack} />

      <OnboardingSection
        header={
          <div className="space-y-2">
            <h2 className="text-title-large text-on-surface">
              {t("onboarding.passcodeCreate.title", "Pass code")}
            </h2>
            <p className="text-body-medium text-on-surface-var">
              {t(
                "onboarding.passcodeCreate.subtitle",
                "Protect your account with a security code"
              )}
            </p>
            {contextNote ? (
              <p className="text-body-small text-on-surface-var">{contextNote}</p>
            ) : null}
          </div>
        }
        footer={
          <Button size="lg" disabled={!valid} onClick={handleContinue}>
            {t("onboarding.passcodeCreate.continue", "Continue")}
          </Button>
        }
      >
        <h3 className="text-label-md text-center mb-4">
          {t("onboarding.passcodeCreate.create", "Create passcode")}
        </h3>

        <div className="flex justify-center">
          <div className="afrimed-otp">
            <InputOtp
              value={pass}
              onChange={(next) => {
                onTelemetry?.("field_changed", { field: "passcode_create", value_recorded: false });
                setPass(next);
              }}
              length={6}
              tone="surface"
              type="password"
              inputMode="numeric"
              autoFocus
            />
          </div>
        </div>
      </OnboardingSection>
    </>
  );
}
